<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { inviteCode } from '../api/http'
import AuthFrame from '../components/AuthFrame.vue'
import ScanButton from '../components/ScanButton.vue'
import { connectTo } from '../lib/connect'
import { forgetServer, isApp, parseServerInput, serverBase, serverHost } from '../platform'

const router = useRouter()
const link = ref('')
const scanError = ref<string | null>(null)

function go(): void {
  const raw = link.value.trim()
  const [path = '', fragment] = raw.split('#')
  const device = path.match(/\/link\/([A-Za-z0-9_-]+)\/?$/)
  // Keep the fragment: it carries the new device's name.
  if (device?.[1]) return void router.push({ name: 'link', params: { token: device[1] }, hash: fragment ? `#${fragment}` : '' })
  const code = inviteCode(raw)
  if (code) void router.push({ name: 'invite', params: { code } })
}

/** A scanned invite or device link; one from another server switches to it (we're signed out). */
async function scanned(text: string): Promise<void> {
  scanError.value = null
  const origin = parseServerInput(text)?.origin
  if (origin && origin !== serverBase()) {
    scanError.value = (await connectTo(text)) || null
    return
  }
  link.value = text
  go()
}

function changeServer(): void {
  forgetServer()
  window.location.replace('/connect')
}
</script>

<template>
  <AuthFrame testid="landing" caption="private · e2ee">
      <h1 class="mb-2 text-3xl font-light tracking-tight">A private place for you and your friends.</h1>
      <p class="mb-8 text-sm text-text-muted">
        A private place for you and your friends. hrth is invite-only — ask a friend for a link.
      </p>
      <form class="space-y-3" @submit.prevent="go">
        <label class="label" for="invite">Have an invite link?</label>
        <input id="invite" v-model="link" class="input" placeholder="https://…/i/…" autocomplete="off" />
        <button class="btn-primary w-full" :disabled="!link.trim()">Continue</button>
      </form>
      <div class="mt-3"><ScanButton @scanned="scanned" /></div>
      <p v-if="scanError" class="mt-2 border border-signal px-3 py-2 text-sm text-error">{{ scanError }}</p>
      <div class="mt-8 space-y-2 border-t border-border pt-4 text-xs text-text-muted">
        <p>Adding a device? Open the link from <em>Settings › Link a new device</em> on your other device.</p>
        <p>
          Lost your device?
          <router-link to="/recover" class="text-text-primary underline underline-offset-2" data-testid="to-recover"
            >Recover your account</router-link
          >
        </p>
        <p v-if="isApp">
          On <span class="mono text-text-secondary">{{ serverHost() }}</span> ·
          <button type="button" class="text-text-primary underline underline-offset-2" data-testid="change-server" @click="changeServer">
            Change server
          </button>
        </p>
      </div>
  </AuthFrame>
</template>
